// @flow

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Layer, Feature } from 'react-mapbox-gl';
import { connect } from 'react-redux';
import { Map, Set } from 'immutable';
import {
  selectRowLevelChangepoint,
  selectOriginalListings,
  Row,
  selectShouldShowRows,
  selectOnListingClick,
  selectOnListingsHover,
  selectListingRowsFilter,
  selectFavoriteListingRowsFilter,
  selectSelectedListingRowFilter,
  selectSelectedRow,
  selectFavoriteRows,
} from '../modules/map';

import type { Listing } from '../types';

type Props = {
  rowLevelChangepoint: number,
  listings: Map<Row | string, Set<Listing>>,
  showingRows: boolean,
  onListingClick: (listing: Listing) => void,
  // eslint-disable-next-line react/no-unused-prop-types
  onListingsHover: (listings: Array<Listing>) => void,
  listingFilter: Array<any>,
  favoriteListingFilter: Array<any>,
  selectedListingFilter: Array<any>,
  selected: Map<string, string> | null,
  favorites: Set<Map<string, string>>,
};

type State = {
  hoveredFeatures: Array<any>,
};

const rowFilter = ['all',
  ['==', ['geometry-type'], 'Polygon'],
  ['==', ['get', 'type'], 'row'],
];

const listingFill = color => ({
  'fill-color': color,
  'fill-antialias': true,
  'fill-outline-color': '#fff',
});

const paint = {
  stroke: {
    'line-color': '#fff',
    'line-width': [
      'interpolate', ['exponential', 1], ['zoom'],
      5, 0.75,
      7, 1.5,
    ],
  },
  fill: {
    'fill-color': 'rgb(224,224,224)',
    'fill-antialias': true,
    'fill-outline-color': '#fff',
  },
  fillListingNormal: listingFill('#a5cc77'),
  fillListingFavorite: listingFill('#e65889'),
  fillListingSelected: listingFill('#f6805b'),
  fillListingHover: {
    'fill-color': ['match', ['get', 'category'],
      'favorite', '#a31545',
      'selected', '#b23d18',
      '#618834', // normal
    ],
  },
};

class RowStyles extends PureComponent<Props, State> {
  static contextTypes = {
    map: PropTypes.object.isRequired,
  };

  state = {
    hoveredFeatures: [],
  };

  componentWillMount() {
    this.context.map.on('mousemove', 'row-listing-fill', this.hoverFeature);
    this.context.map.on('mouseleave', 'row-listing-fill', this.hoverFeature);
    this.context.map.on('click', 'row-listing-fill', this.clickFeature);
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.showingRows) {
      this.dispatchListingsHover(this.state.hoveredFeatures[0], nextProps);
    }
  }

  componentWillUnmount() {
    this.context.map.off('mousemove', 'row-listing-fill', this.hoverFeature);
    this.context.map.off('mouseleave', 'row-listing-fill', this.hoverFeature);
    this.context.map.off('click', 'row-listing-fill', this.clickFeature);
  }

  get hoveredFeatures() {
    const category = this.hoveredListingType;
    const properties = { category };
    return this.state.hoveredFeatures.map(feature => (
      <Feature
        key={feature.id}
        coordinates={feature.geometry.coordinates}
        properties={properties}
      />
    ));
  }

  get hoveredListingType() {
    const [feature] = this.state.hoveredFeatures;

    if (feature) {
      const listing = Row(feature);
      // $FlowFixMe
      if (listing.equals(this.props.selected)) return 'selected';
      if (this.props.favorites.has(listing)) return 'favorite';
    }

    return 'normal';
  }

  getFeatureSet = ({ properties: { section, row } }) => this.context.map
    .queryRenderedFeatures({
      layers: ['row-listing-fill'],
      filter: ['all',
        ['==', ['get', 'type'], 'row'],
        ['==', ['get', 'section'], section],
        ['==', ['get', 'row'], row],
      ],
    })

  dispatchListingsHover(feature, { showingRows, onListingsHover, listings } = this.props) {
    if (showingRows) {
      const rowListings = feature && listings.get(Row(feature));
      onListingsHover(rowListings ? rowListings.toArray() : []);
    }
  }

  hoverFeature = ({ features }) => {
    if (features) {
      const [feature] = features;

      if (!this.state.hoveredFeatures.find(({ id }) => feature.id === id)) {
        this.setState({ hoveredFeatures: this.getFeatureSet(feature) });
        this.dispatchListingsHover(feature);
      }
    } else {
      this.dispatchListingsHover();
      this.setState({ hoveredFeatures: [] });
    }
  }

  clickFeature = ({ features: [feature] }) => {
    if (this.props.showingRows) {
      const rowListings = this.props.listings.get(Row(feature));
      if (rowListings) this.props.onListingClick(...rowListings.toArray());
    }
  }

  render() {
    if (this.props.showingRows) {
      this.context.map.getCanvas().style.cursor = this.state.hoveredFeatures.length ? 'pointer' : '';
    }

    return (
      <div>
        <Layer
          id="row-stroke"
          before="section-stroke"
          type="line"
          sourceId="venue"
          sourceLayer="data"
          filter={rowFilter}
          paint={paint.stroke}
          minZoom={this.props.rowLevelChangepoint}
        />
        <Layer
          id="row-fill"
          before="stage"
          type="fill"
          sourceId="venue"
          sourceLayer="data"
          filter={rowFilter}
          paint={paint.fill}
          minZoom={this.props.rowLevelChangepoint}
        />
        <Layer
          id="row-listing-fill"
          before="stage"
          type="fill"
          sourceId="venue"
          sourceLayer="data"
          filter={this.props.listingFilter}
          paint={paint.fillListingNormal}
          minZoom={this.props.rowLevelChangepoint}
        />
        <Layer
          id="row-favorite-listing-fill"
          before="stage"
          type="fill"
          sourceId="venue"
          sourceLayer="data"
          filter={this.props.favoriteListingFilter}
          paint={paint.fillListingFavorite}
          minZoom={this.props.rowLevelChangepoint}
        />
        <Layer
          id="row-selected-listing-fill"
          before="stage"
          type="fill"
          sourceId="venue"
          sourceLayer="data"
          filter={this.props.selectedListingFilter}
          paint={paint.fillListingSelected}
          minZoom={this.props.rowLevelChangepoint}
        />
        <Layer
          id="row-listing-hover"
          before="stage"
          type="fill"
          paint={paint.fillListingHover}
          minZoom={this.props.rowLevelChangepoint}
        >
          {this.hoveredFeatures}
        </Layer>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  rowLevelChangepoint: selectRowLevelChangepoint(state),
  listings: selectOriginalListings(state),
  showingRows: selectShouldShowRows(state),
  onListingClick: selectOnListingClick(state),
  onListingsHover: selectOnListingsHover(state),
  listingFilter: selectListingRowsFilter(state),
  favoriteListingFilter: selectFavoriteListingRowsFilter(state),
  selectedListingFilter: selectSelectedListingRowFilter(state),
  favorites: selectFavoriteRows(state),
  selected: selectSelectedRow(state),
});

export default connect(mapStateToProps)(RowStyles);



// WEBPACK FOOTER //
// ./src/styles/rows.js